// Ported from tkt-back-office src/lib/passkey.js — better-auth's own passkey plugin, the same one
// the backend registers. See the header there for why each guard exists.
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { authClient } from './authClient.js';

// ─────────────────────────────────────────────────────────────────────────────
// PASSKEY SIGN-IN — signing in by being yourself.
//
// NOT lib/biometric.js. That file is a step-up in front of ONE action (box-office confirm)
// on a hand-rolled `/api/biometric/*` endpoint whose enrolment trusts the session alone (see
// its header). It must never grow into an authentication path, and this must never be built
// on it.
//
// This runs better-auth's OWN passkey plugin — the same plugin the backend registers and the
// sellers dashboard signs in with. better-auth verifies the challenge and the signature and
// mints the session itself, so what comes out is the ordinary session every other request in
// this console already carries. No second session concept, and no ceremony of our own: an
// authentication protocol is the last place to keep a private reimplementation.
//
// THE FRICTION THIS EXISTS TO REMOVE. An operator who opens the console every morning types
// their address, alt-tabs to their inbox, waits for a code, and types six digits — every
// time the session lapses. The passkey is on their laptop already.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What this browser can actually do, asked as the three SEPARATE questions they are.
 *
 * Conflating them produces the worst version of the feature: a "Sign in with Touch ID"
 * button on a desktop with no sensor, or a silent autofill ceremony on a browser that
 * cannot do conditional mediation and therefore throws instead of staying quiet.
 */
export const passkeySupport = async () => {
  if (typeof window === 'undefined' || !browserSupportsWebAuthn()) {
    return { supported: false, platform: false, conditional: false };
  }
  if (!window.isSecureContext) {
    // HTTPS or localhost. Its own branch because the failure is otherwise a bare
    // SecurityError thrown from deep inside the ceremony.
    return { supported: false, platform: false, conditional: false, reason: 'secure-context' };
  }
  const [platform, conditional] = await Promise.all([
    platformAuthenticatorIsAvailable().catch(() => false),
    browserSupportsWebAuthnAutofill().catch(() => false),
  ]);
  return { supported: true, platform, conditional };
};

/** What the operator's own device calls this. Named from the platform, because "authenticate
 *  with WebAuthn" is not a thing anyone has ever seen on their phone. */
export const passkeyLabel = () => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const touch = (typeof navigator !== 'undefined' && navigator.maxTouchPoints) > 1;
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && touch)) return 'Touch ID';
  if (/iPhone|iPod/.test(ua)) return 'Face ID';
  if (/Macintosh/.test(ua)) return 'Touch ID';
  if (/Android/.test(ua)) return 'fingerprint';
  if (/Windows/.test(ua)) return 'Windows Hello';
  return 'your passkey';
};

/** A name the operator will recognise in a list of their own devices months from now. */
const deviceName = () => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android device';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux machine';
  return 'This device';
};

/**
 * A cancelled prompt is NOT an error.
 *
 * Every one of these is the operator deciding not to — closing the sheet, hitting escape,
 * walking away until it times out, or us aborting the background ceremony on purpose.
 * Surfacing it as a red box teaches them the feature is broken. It is the single most
 * common outcome of any WebAuthn prompt and it must be silent.
 *
 * `AUTH_CANCELLED` is the plugin's own code: signInPasskey catches every ceremony failure
 * and RETURNS it as that code rather than throwing, so this is the shape the caller sees.
 */
export const isCancellation = (e) => {
  const code = e?.code || e?.error?.code;
  if (code === 'AUTH_CANCELLED') return true;
  const name = e?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') return true;
  const msg = String(e?.message || e?.error?.message || '').toLowerCase();
  return msg.includes('cancel') || msg.includes('abort') || msg.includes('not allowed') || msg.includes('timed out');
};

/** The platform refusing to make a second credential for a pair it already holds. Means
 *  "already set up", never a failure — better-auth seeds excludeCredentials, so the
 *  authenticator answers this itself, before any prompt. */
export const isAlreadyEnrolled = (e) => {
  if (e?.name === 'InvalidStateError') return true;
  const msg = String(e?.message || e?.error?.message || '').toLowerCase();
  return msg.includes('previously registered') || msg.includes('already registered') || msg.includes('already exists');
};

/**
 * End any ceremony currently in flight.
 *
 * WebAuthn allows exactly ONE outstanding request per page. The conditional ceremony below
 * stays pending for as long as the sign-in form is on screen, so without this an operator
 * who then taps "Sign in with Touch ID" gets `NotAllowedError: a request is already
 * pending` — the explicit button broken by the invisible one.
 *
 * This is why @simplewebauthn/browser is a DIRECT dependency pinned to ^13, the same range
 * the plugin resolves: its abort service is a module singleton, so cancelling only reaches
 * the plugin's ceremony while both import the SAME copy. Installed at 11 (copied from the
 * dashboard) it deduped to two copies, two singletons, and a cancel that silently did
 * nothing — verified in the tree, not assumed.
 */
export const cancelCeremony = () => { try { WebAuthnAbortService.cancelCeremony(); } catch { /* nothing in flight */ } };

// ── The device hint ──────────────────────────────────────────────────────────
// ONLY ever an optimisation: it decides whether to OFFER the one-tap button up front. It is
// never a precondition for signing in, because the browser — not this key — is the authority
// on which credentials exist. Sign-in works with the hint absent (cleared storage, a fresh
// profile, a passkey synced from another device), which is exactly when a hint-gated design
// silently falls back to email codes while a working passkey sits on the machine.
const HINT_KEY = 'momo.backoffice.passkeyEmail';
export const readPasskeyHint = () => { try { return localStorage.getItem(HINT_KEY) || ''; } catch { return ''; } };
export const savePasskeyHint = (email) => { try { if (email) localStorage.setItem(HINT_KEY, String(email).toLowerCase()); } catch { /* private mode — a hint is a convenience */ } };
export const clearPasskeyHint = () => { try { localStorage.removeItem(HINT_KEY); } catch { /* non-fatal */ } };

/**
 * Enrol THIS device for the ALREADY SIGNED-IN operator.
 *
 * Offered right after a code sign-in, because that is the one moment the cost is zero (they
 * are already here, already verified) and the benefit is plain (never do this again).
 *
 * `authenticatorAttachment: 'platform'` keeps the browser on the built-in sensor instead of
 * opening the "phone / security key / another device" branch. The backend pins the same
 * thing server-side, so this is the client agreeing rather than the client deciding.
 */
export const enrollPasskey = async ({ name } = {}) => {
  cancelCeremony();
  const { error } = await authClient.passkey.addPasskey({
    name: name || deviceName(),
    authenticatorAttachment: 'platform',
  });
  if (error) throw error;
  return { name: name || deviceName() };
};

/**
 * Sign in with a passkey. ONE ceremony, two mediations.
 *
 * `conditional: true` is the frictionless one — the browser offers the operator's passkey
 * inside the email field's own autofill dropdown, with no button to find and no prompt
 * thrown at anyone who does not have one. It needs an input carrying the `webauthn`
 * autocomplete token to attach to, and it stays pending until picked or cancelled.
 *
 * The plugin RETURNS errors rather than throwing them, so both shapes are normalised into a
 * throw here and every caller can use one try/catch.
 */
export const signInWithPasskey = async ({ conditional = false } = {}) => {
  if (!conditional) cancelCeremony(); // an explicit tap supersedes the background ceremony
  const res = await authClient.signIn.passkey({ autoFill: conditional, returnWebAuthnResponse: true });
  if (res?.error) throw res.error;
  const data = res?.data;
  const user = data?.session?.user ?? data?.user ?? null;
  restateAccount(res?.webauthn?.response, user);
  return user;
};

/**
 * TELL THE DEVICE WHOSE PASSKEY IT JUST USED.
 *
 * The phone's passkey list labels each one with the account name it was saved under, and until
 * the backend fixed it (auth.ts, generate-register-options) that was the DEVICE — so every account
 * read "iPhone". New passkeys are saved under the account; this corrects the old ones on the device
 * itself, each the next time it signs in. Fire-and-forget: a browser without the Signal API, or an
 * rpId that is not this credential's, simply does nothing.
 */
const restateAccount = (assertion, user) => {
  const userId = assertion?.response?.userHandle;
  const name = user?.email || user?.phoneNumber;
  if (!userId || !name || typeof window === 'undefined' || typeof window.PublicKeyCredential?.signalCurrentUserDetails !== 'function') return;
  // The rpId is a suffix of this host (tkt.ug for backoffice.tkt.ug). Each candidate is tried; one
  // that names no credential of ours is a no-op, and one the browser will not accept is caught.
  const labels = window.location.hostname.split('.');
  const rpIds = labels.length === 1 ? labels : labels.slice(0, -1).map((_, i) => labels.slice(i).join('.'));
  for (const rpId of rpIds) {
    window.PublicKeyCredential.signalCurrentUserDetails({ rpId, userId, name, displayName: name }).catch(() => {});
  }
};
